import { useEffect, useMemo, useState } from 'react'
import './BannersManager.sass'
import StatusMessage, { useStatusMessage } from '../../../../../ui/StatusMessage/StatusMessage'
import getDoc from '../../../../../utils/db/getDoc'
import { createForm } from '../../../../../utils/newforms/createForm'
import getFieldRenderObject from '../../../../../utils/newforms/render/getFieldRenderObject'
import Spinner from '../../../../../ui/Spinner/Spinner'
import Button from '../../../../../ui/Button/Button'
import { isNull, omit } from 'lodash'
import ModulePanel from '../../../../../ui/ModulePanel/ModulePanel'
import FormRender from '../../../../../utils/newforms/render/FormRender'
import { withRouter } from 'react-router-dom'
import { format } from 'date-fns'
import PhotoManager from '../../../../PhotoManager/PhotoManager'
import syncForm from '../../../../../utils/newforms/changed/syncForm'
import isFormValid from '../../../../../utils/newforms/validation/isFormValid'
import getFormValues from '../../../../../utils/newforms/getFormValues'
import { updateDoc } from '../../../../../utils/db/updateDoc'
import uploadPhotos from '../../../../../_deprecated/Nauka/NaukaManager/functions/uploadPhotos'
import NotificationBlock from '../../../../../ui/NotificationBlock/NotificationBlock'
import DeleteBannerPopUp from '../AddBanner/DeleteBannerPopUp'
import BannersSitePreview from '../HeroPreview/BannersSitePreview'

/** Site Hero tile size at 4-col / 1200 grid: (1200 - 3×16) / 4 = 288. */
const PREVIEW_TILE_W = 288
const PREVIEW_TILE_H = 384
const PREVIEW_GAP = 16

function resolvePhotoPreviewUrl(photos) {
  if (!Array.isArray(photos) || !photos.length) return null
  const photo = photos.find((p) => p && p.isAvatar) || photos[0]
  if (!photo) return null
  return photo.imageUrl || photo.publicUrl || photo.url || photo.fileUrl || null
}

function BannerSeriesPreview({ firstPhotos, secondPhotos }) {
  const firstUrl = resolvePhotoPreviewUrl(firstPhotos)
  const secondUrl = resolvePhotoPreviewUrl(secondPhotos)
  const tiles = [
    { key: '1', url: firstUrl, label: 'Банер 1' },
    { key: '2', url: secondUrl, label: 'Банер 2' },
  ]

  return (
    <div className="BannerSeriesPreview">
      <div
        className="BannerSeriesPreview-Row"
        style={{
          width: PREVIEW_TILE_W * 2 + PREVIEW_GAP,
          gap: PREVIEW_GAP,
        }}
      >
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="BannerSeriesPreview-Tile"
            style={{ width: PREVIEW_TILE_W, height: PREVIEW_TILE_H }}
          >
            {tile.url ? (
              <img src={tile.url} alt={tile.label} />
            ) : (
              <div className="BannerSeriesPreview-Empty">{tile.label}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function sanitizePhotos(photos) {
  return (photos || []).filter((p) => !p.needUpload).map((p) => omit(p, ['file', 'fileUrl']))
}

/** Map legacy single-slot docs into first/second creatives for the form. */
function normalizeSeriesFormData(data = {}) {
  const firstPhotos =
    Array.isArray(data.firstPhotos) && data.firstPhotos.length
      ? data.firstPhotos
      : Array.isArray(data.desktopPhotos) && data.desktopPhotos[0]
        ? [data.desktopPhotos[0]]
        : []
  const secondPhotos =
    Array.isArray(data.secondPhotos) && data.secondPhotos.length
      ? data.secondPhotos
      : Array.isArray(data.desktopPhotos) && data.desktopPhotos[1]
        ? [data.desktopPhotos[1]]
        : []
  return {
    ...data,
    firstPhotos,
    secondPhotos,
  }
}

function BannersManager({ ...router }) {
  const bannerId = router.match.params.bannerId

  const [form, setForm] = useState(null)
  const [showErrors] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [showBannerDelete, setShowBannerDelete] = useState(false)

  useEffect(() => {
    if (bannerId) {
      getDoc({ path: 'settings/banners/list', docId: bannerId }).then((data) => {
        setForm(
          createForm({
            formPattern: new BannerForm(),
            formData: normalizeSeriesFormData(data),
          })
        )
      })
    }
  }, [bannerId])

  const seriesDraft = useMemo(() => {
    if (!form) return null
    return {
      name: form.name?.value || '',
      link: form.link?.value || '',
      isActive: Boolean(form.isActive?.value),
      isTerm: Boolean(form.isTerm?.value),
      dateStart: form.dateStart?.value || '',
      timeStart: form.timeStart?.value || '',
      dateEnd: form.dateEnd?.value || '',
      timeEnd: form.timeEnd?.value || '',
      firstPhotos: form.firstPhotos?.values || [],
      secondPhotos: form.secondPhotos?.values || [],
      pos: form.pos?.value,
    }
  }, [form])

  const onSubmit = async () => {
    if (!isFormValid({ form })) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Будь ласка, заповніть усі поля форми',
        closeAfter: 5000,
      })
    }
    setIsLoading(true)
    const data = getFormValues({ form })

    const firstPhotos = sanitizePhotos(data.firstPhotos)
    const secondPhotos = sanitizePhotos(data.secondPhotos)
    // Keep desktopPhotos as [first, second] for legacy readers / older site builds.
    const desktopPhotos = [...firstPhotos, ...secondPhotos]

    const values = {
      ...data,
      updated: new Date(),
      firstPhotos,
      secondPhotos,
      desktopPhotos,
      tabletPhotos: [],
      mobilePhotos: [],
    }
    const opRef = updateDoc({
      path: 'settings/banners/list',
      docId: bannerId,
      data: values,
    })

    opRef
      .then((docId) =>
        Promise.all([
          uploadPhotos({
            photos: data.firstPhotos,
            docId,
            docPath: 'settings/banners/list',
            storagePath: 'settings/banners',
            propName: 'firstPhotos',
          }),
          uploadPhotos({
            photos: data.secondPhotos,
            docId,
            docPath: 'settings/banners/list',
            storagePath: 'settings/banners',
            propName: 'secondPhotos',
          }),
        ])
          .then(() => getDoc({ path: 'settings/banners/list', docId: bannerId }))
          .then((fresh) =>
            updateDoc({
              path: 'settings/banners/list',
              docId: bannerId,
              data: {
                desktopPhotos: [...(fresh?.firstPhotos || []), ...(fresh?.secondPhotos || [])],
              },
            })
          )
          .then(() => {
            setIsLoading(false)
            setStatusMessage({
              show: true,
              type: 'success',
              message: 'Дані збережено',
              closeAfter: 5000,
            })
            window.scrollTo({ top: 0, behavior: 'smooth' })
          })
      )
      .catch((error) => {
        console.log('🚀 ~ onSubmit ~ error:', error)
        setIsLoading(false)
      })
  }

  return (
    <div className="BannersManager">
      {!isNull(form) ? (
        <>
          {!form.isActive.value && (
            <NotificationBlock
              show
              theme="info"
              title="Серія не активна"
              content="Цю серію можна переглянути тільки у режимі адміністратора."
            />
          )}
          {statusMessage.show && (
            <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
          )}
          {showBannerDelete && (
            <DeleteBannerPopUp
              bannerId={bannerId}
              title={form.name.value}
              closePopUp={() => setShowBannerDelete(false)}
            />
          )}
          <ModulePanel
            title={form.name.value}
            buttons={<Button theme="circle" fill="white" icon="trash-alt" onClick={() => setShowBannerDelete(true)} />}
          >
            <div className="MainInfo AdditionalInfo-Container">
              <FormRender
                sections={[
                  {
                    fields: ['isActive', 'name', 'link'],
                  },
                ]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />

              <div className="TimeSettings">
                <FormRender
                  sections={[
                    {
                      fields: ['isTerm', 'dateStart', 'timeStart', 'dateEnd', 'timeEnd'],
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  errors={showErrors}
                />
              </div>

              <div className="BannersManager-SeriesRow">
                <div className="BannersManager-SeriesCol">
                  <p className="Title">Банер 1 (3×4)</p>
                  <PhotoManager
                    projectId={bannerId}
                    projectPath="settings/banners/list"
                    formData={form ? form.firstPhotos.values : null}
                    propName="firstPhotos"
                    syncState={(moduleState) =>
                      syncForm({
                        propIdentifier: 'firstPhotos',
                        form,
                        setForm,
                        newForm: moduleState,
                      })
                    }
                  />
                </div>

                <div className="BannersManager-SeriesCol">
                  <p className="Title">Банер 2 (3×4)</p>
                  <PhotoManager
                    projectId={bannerId}
                    projectPath="settings/banners/list"
                    formData={form ? form.secondPhotos.values : null}
                    propName="secondPhotos"
                    syncState={(moduleState) =>
                      syncForm({
                        propIdentifier: 'secondPhotos',
                        form,
                        setForm,
                        newForm: moduleState,
                      })
                    }
                  />
                </div>

                <div className="BannersManager-SeriesCol BannersManager-SeriesCol_preview">
                  <p className="Title">Прев’ю серії</p>
                  <BannerSeriesPreview
                    firstPhotos={form.firstPhotos.values}
                    secondPhotos={form.secondPhotos.values}
                  />
                </div>
              </div>
            </div>
          </ModulePanel>
          <BannersSitePreview seriesId={bannerId} seriesDraft={seriesDraft} />
          <div className="Buttons">
            <Button
              title="Зберегти"
              theme="solid"
              fill="accent"
              iconPosition="right"
              size={32}
              isLoading={isLoading}
              onClick={onSubmit}
            />
          </div>
        </>
      ) : (
        <Spinner type="module" />
      )}
    </div>
  )
}

class BannerForm {
  constructor() {
    this.name = {
      field: {
        fieldId: 'name',
        fieldType: 'input',
        inputType: 'text',
        label: 'Назва серії',
        required: true,
      },
      render: getFieldRenderObject(),
    }
    this.link = {
      field: {
        fieldId: 'link',
        fieldType: 'input',
        inputType: 'text',
        label: 'Посилання',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.dateStart = {
      field: {
        fieldId: 'dateStart',
        fieldType: 'input',
        inputType: 'text',
        mask: 'date',
        label: 'Дата початку',
        required: false,
        value: format(new Date(), 'dd.MM.yyyy'),
      },
      render: getFieldRenderObject(),
    }
    this.timeStart = {
      field: {
        fieldId: 'timeStart',
        fieldType: 'input',
        inputType: 'text',
        mask: 'time',
        label: 'Час початку',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.dateEnd = {
      field: {
        fieldId: 'dateEnd',
        fieldType: 'input',
        inputType: 'text',
        mask: 'date',
        label: 'Дата закінчення',
        required: false,
        value: format(new Date(), 'dd.MM.yyyy'),
      },
      render: getFieldRenderObject(),
    }
    this.timeEnd = {
      field: {
        fieldId: 'timeEnd',
        fieldType: 'input',
        inputType: 'text',
        mask: 'time',
        label: 'Час закінчення',
        required: false,
      },
      render: getFieldRenderObject(),
    }

    this.firstPhotos = {
      field: { fieldId: 'firstPhotos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.secondPhotos = {
      field: { fieldId: 'secondPhotos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.desktopPhotos = {
      field: { fieldId: 'desktopPhotos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.tabletPhotos = {
      field: { fieldId: 'tabletPhotos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.mobilePhotos = {
      field: { fieldId: 'mobilePhotos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.isActive = {
      field: {
        fieldId: 'isActive',
        fieldType: 'checkbox',
        label: 'Задіяти на сайті',
      },
      render: getFieldRenderObject(),
    }
    this.isTerm = {
      field: {
        fieldId: 'noTerm',
        fieldType: 'checkbox',
        label: 'Термін показу',
        value: false,
      },
      render: getFieldRenderObject(),
    }
    this.created = {
      field: { fieldId: 'created', value: new Date() },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.updated = {
      field: { fieldId: 'updated', value: new Date() },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.pos = {
      field: { fieldId: 'pos' },
      render: getFieldRenderObject({ isSystem: true }),
    }
  }
}

export default withRouter(BannersManager)
